import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import { API_URL } from '../components/config';
import { CIP_APP_URL, CIP_RSMIN_TENANT_SLUG } from '../components/config';
import { getViewAsMode, isPublicViewMode } from '../utils/viewMode';
import './HarvestingPage.css';

const HarvestingPage = () => {
  const location = useLocation();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCipEmbed, setShowCipEmbed] = useState(false);
  const viewAsMode = getViewAsMode(location.search);
  const publicPreview = isPublicViewMode(location.search);
  const previewQuery = viewAsMode ? `?viewAs=${viewAsMode}` : '';

  const stripHtml = (value) => {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        console.log('Fetching harvesting posts...');
        const response = publicPreview
          ? await axios.get(`${API_URL}/api/harvesting-posts/public`)
          : await api.get('/api/harvesting-posts/public');
        console.log('Harvesting posts response:', response.data);
        setBlogPosts((response.data.posts || []).slice(0, 3)); // Fetch latest 3 posts
        setLoading(false);
      } catch (err) {
        console.error('Error fetching harvesting posts:', err);
        setError(err.response?.data?.message || 'Error fetching blog posts');
        setLoading(false);
      }
    };
    fetchBlogPosts();
  }, [publicPreview]);

  const authToken = localStorage.getItem('authToken');
  const cipReady = !!CIP_APP_URL;
  const canBridge = cipReady && !!authToken;

  const cipBase = CIP_APP_URL ? CIP_APP_URL.replace(/\/$/, '') : '';
  const returnUrl = window.location.href;
  const nextPath = `/t/${CIP_RSMIN_TENANT_SLUG}/harvesting?embedded=1&rsminReturn=${encodeURIComponent(returnUrl)}&rsminLabel=${encodeURIComponent('Back to RSMIN Harvesting')}`;
  const cipBridgeUrl = canBridge
    ? `${cipBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(nextPath)}`
    : '';

  const nextPathLog = `/t/${CIP_RSMIN_TENANT_SLUG}/harvesting?embedded=1&mode=contribute&rsminReturn=${encodeURIComponent(returnUrl)}&rsminLabel=${encodeURIComponent('\u2190 Back to Harvesting Portal')}`;
  const cipLogUrl = canBridge
    ? `${cipBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(nextPathLog)}`
    : '';

  return (
    <div className="harvesting-page-container">
      <div className="harvesting-header">
        <h1>Harvesting Portal</h1>
        <p className="subtitle">Resources, information and latest updates for RSMIN community harvesting</p>
      </div>

      {/* Latest Blog Posts */}
      <div className="latest-blog-section">
        <div className="section-header">
          <h2>Latest Updates</h2>
          <Link to={`/harvesting/blog${previewQuery}`} className="view-all-link">View All Posts</Link>
        </div>
        
        {loading ? (
          <div className="loading-spinner">Loading posts...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : blogPosts.length === 0 ? (
          <p className="no-posts-message">No posts available at this time.</p>
        ) : (
          <div className="blog-post-cards">
            {blogPosts.map((post) => (
              <div key={post._id} className="blog-post-card">
                {post.imageUrl && (
                  <div className="blog-post-image">
                    <img src={post.imageUrl} alt={post.title} />
                  </div>
                )}
                <div className="post-date">
                  {new Date(post.publishDate).toLocaleDateString('en-US', {
                    timeZone: 'America/Toronto',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <h3>{post.title}</h3>
                <p className="post-excerpt">
                  {stripHtml(post.content).length > 120 
                    ? stripHtml(post.content).substring(0, 120) + '...' 
                    : stripHtml(post.content)}
                </p>
                <Link to={`/harvesting/blog/${post._id}${previewQuery}`} className="read-more-link">
                  Read More →
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Harvesting Resources */}
      <div className="resources-section">
        <h2>Harvesting Resources</h2>

        <div className="log-harvest-hero">
          <div className="log-harvest-hero-inner">
            <div className="log-harvest-hero-text">
              <span className="log-harvest-icon-lg">🌿</span>
              <div>
                <h3>Log Your Harvesting Activity</h3>
                <p>Record a harvest, count species, or report an environmental concern. Your records are yours — view, edit, or remove them at any time.</p>
              </div>
            </div>
            {!cipReady && (
              <div className="cip-embed-warning">
                CIP integration not configured. Set REACT_APP_CIP_APP_URL to enable.
              </div>
            )}
            {cipReady && !authToken && (
              <div className="cip-embed-warning">
                Log in to record your harvesting activity.
              </div>
            )}
            {canBridge && (
              <div className="log-harvest-hero-actions">
                <a href={cipLogUrl} target="_blank" rel="noreferrer" className="log-harvest-cta">
                  + Log Harvesting Activity
                </a>
                <button type="button" className="tile-button secondary" onClick={() => setShowCipEmbed((v) => !v)}>
                  {showCipEmbed ? 'Hide' : 'Open Here'}
                </button>
              </div>
            )}
          </div>
          {showCipEmbed && canBridge && (
            <div className="cip-embed-frame-wrap">
              <iframe
                title="Log Harvesting Activity"
                src={cipLogUrl}
                className="cip-embed-frame"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          )}
        </div>
        
        <div className="resource-tiles">
          <div className="resource-tile">
            <div className="tile-icon">ℹ️</div>
            <h3>Information Hub</h3>
            <p>Access guidelines, permits, and regulations for sustainable harvesting.</p>
            <Link to={`/harvesting/information${previewQuery}`} className="tile-button">
              Learn More
            </Link>
          </div>
          
          <div className="resource-tile">
            <div className="tile-icon">🎓</div>
            <h3>Education Resources</h3>
            <p>Find workshops, courses, and educational materials on traditional harvesting.</p>
            <Link to={`/harvesting/education${previewQuery}`} className="tile-button">
              Browse Resources
            </Link>
          </div>
          
          {/* Temporarily hidden - Community Hub coming soon
          <div className="resource-tile">
            <div className="tile-icon">💬</div>
            <h3>Community Hub</h3>
            <p>Connect with other harvesters, share experiences, and participate in discussions.</p>
            <Link to="/harvesting/community" className="tile-button">
              Join Conversations
            </Link>
          </div>
          */}
          
          <div className="resource-tile">
            <div className="tile-icon">🌿</div>
            <h3>Log Harvesting Activity</h3>
            <p>Record your harvests, species counts, and environmental observations. Your data, your control.</p>
            {canBridge ? (
              <a href={cipLogUrl} target="_blank" rel="noreferrer" className="tile-button">
                Log Activity
              </a>
            ) : (
              <span className="tile-button">Login to Log Activity</span>
            )}
          </div>

          <div className="resource-tile">
            <div className="tile-icon">📞</div>
            <h3>Contact Us</h3>
            <p>Get in touch with our harvesting team for questions, support, and guidance.</p>
            <Link to={`/harvesting/contact${previewQuery}`} className="tile-button">
              Contact Team
            </Link>
          </div>
        </div>
      </div>
      
      {/* Quick Links Section */}
      <div className="quick-links-section">
        <h2>Quick Links</h2>
        <div className="quick-links">
          <Link to={`/harvesting/blog${previewQuery}`} className="quick-link">Latest Harvesting Posts</Link>
          <Link to={`/harvesting/information${previewQuery}`} className="quick-link">Permits and Information</Link>
          {canBridge ? (
            <a href={cipLogUrl} target="_blank" rel="noreferrer" className="quick-link">Log Harvesting Activity</a>
          ) : (
            <span className="quick-link">Log Harvesting Activity (login required)</span>
          )}
          <Link to={`/harvesting/contact${previewQuery}`} className="quick-link">Contact Harvesting Team</Link>
        </div>
      </div>
    </div>
  );
};

export default HarvestingPage;
